/**
 * One ranked recommendation in the overview list.
 *
 * The score leads, because the list's whole claim is that it is ordered. It
 * used to sit right-aligned at the far end of the row, furthest from the title
 * it belonged to and past four badges of which three said the same thing on
 * every line — "confidence: 92%" repeated down the card is chrome, not
 * information. Severity keeps its badge because it is the one field where the
 * colour carries meaning; the rest read better as a plain line of text.
 */

import { Link } from 'react-router-dom';

import { Badge, SeverityBadge } from '@/components/ui';
import { ArrowRightIcon } from '@/components/icons';
import { cn } from '@/utils/format';
import type { RecommendationSummary } from '@/types/api';

/** Matches the dashboard's "high-impact" counter, so the two cannot disagree. */
const HIGH_IMPACT_SCORE = 40;

export function RecommendationRow({
  recommendation,
  siteId,
}: {
  recommendation: RecommendationSummary;
  siteId: string;
}) {
  const score = Math.round(recommendation.priority_score);
  const highImpact = score >= HIGH_IMPACT_SCORE;

  return (
    <li>
      <Link
        to={`/sites/${siteId}/opportunities`}
        className="group flex items-center gap-3.5 rounded-xl px-2 py-2.5 transition-colors hover:bg-surface-muted"
      >
        {/* The tint is the high-impact threshold made visible: the same line
            the dashboard's counter draws, without spending a badge on it. */}
        <span
          className={cn(
            'flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl border',
            highImpact
              ? 'border-brand-border bg-brand-subtle text-brand'
              : 'border-border-subtle bg-surface-muted text-ink-muted',
          )}
          title={`Priority score ${score} of 100${highImpact ? ' — high impact' : ''}`}
        >
          <span className="tnum text-sm leading-none font-semibold">{score}</span>
          <span className="tnum mt-0.5 text-[9px] leading-none opacity-60">/100</span>
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-ink">{recommendation.title}</span>
            {recommendation.is_ai_generated ? <Badge tone="brand">AI</Badge> : null}
          </span>

          {/* One badge, then plain text. Four badges a row turned the card into
              a wall of pills where nothing stood out. */}
          <span className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-ink-subtle">
            <SeverityBadge severity={recommendation.severity} />
            <span className="truncate">{recommendation.category.replace(/_/g, ' ')}</span>
            <span aria-hidden="true">·</span>
            <span>{recommendation.effort_band} effort</span>
            <span aria-hidden="true">·</span>
            <span>{Math.round(recommendation.confidence * 100)}% confidence</span>
          </span>
        </span>

        <ArrowRightIcon className="h-4 w-4 shrink-0 text-ink-subtle opacity-0 transition-opacity group-hover:opacity-100" />
      </Link>
    </li>
  );
}
